<template>
    <section class="lymphomap_main">
        <div ref="workspaceFrame" class="workspaceFrame" :class="{
            collapsed_chat_list: isWorkspaceCollapsed,
            mobile_tools_open:
                isMobile && isMobileToolbarOpen
        }">
            <ChatUserList @open-mobile-tools="openMobileToolbar" :initial-loading="isChatRoomsLoading" />

            <WorkspaceRightSidebar :expanded="isWorkspaceCollapsed" :is-mobile="isMobile"
                @toggle="handleSidebarToggle" />
        </div>
    </section>
</template>

<script>
import { bus } from '@/main';
import responsiveMixin from '@/mixins/responsive';

import ChatUserList from
    '@/components/private/workspace/chat-user-list/';

import WorkspaceRightSidebar from
    '@/components/private/workspace/common/WorkspaceRightSidebar';

const WORKSPACE_LOAD_ACTIONS = Object.freeze([
    'fetchPinnedChatRooms',
    'fetchNormalChatRooms',
    'getSchedulerListAction'
]);

export default {
    name: 'Workspace',

    components: {
        ChatUserList,
        WorkspaceRightSidebar
    },

    mixins: [responsiveMixin],

    data() {
        return {
            isWorkspaceCollapsed: false,
            isMobileToolbarOpen: false,
            isChatRoomsLoading: true
        };
    },

    created() {
        this.loadWorkspaceData();
    },

    mounted() {
        this.clearWorkspaceNotifications();
    },

    beforeRouteLeave(to, from, next) {
        bus.$emit(
            'workspaceRouterLeaveEvntBus',
            'Router_leave'
        );

        next();
    },

    watch: {
        isMobile(isMobile) {
            this.isMobileToolbarOpen = false;

            /*
             * Never carry the desktop collapsed state
             * into the mobile layout.
             */
            if (
                isMobile &&
                this.isWorkspaceCollapsed
            ) {
                this.setWorkspaceCollapsed(false);
            }
        }
    },

    methods: {
        clearWorkspaceNotifications() {
            bus.$emit('clearMessageNotifications', {});
            bus.$emit('userFriendRequestClear', {});
        },

        async loadWorkspaceData() {
            this.isChatRoomsLoading = true;

            try {
                const results = await Promise.all(
                    WORKSPACE_LOAD_ACTIONS.map(async (action) => {
                        try {
                            await this.$store.dispatch(action);
                            return null;
                        } catch (error) {
                            return {
                                action,
                                error
                            };
                        }
                    })
                );

                const errors = results.filter(Boolean);

                if (errors.length) {
                    this.$emit('load-error', errors);
                }

                return errors;
            } finally {
                this.isChatRoomsLoading = false;
            }
        },

        setWorkspaceCollapsed(collapsed) {
            const nextState = Boolean(collapsed);

            if (
                this.isWorkspaceCollapsed === nextState
            ) {
                return;
            }

            this.isWorkspaceCollapsed = nextState;

            bus.$emit(
                'collapseActvToggleStateBus',
                nextState
            );
        },

        openMobileToolbar() {
            if (!this.isMobile) {
                return;
            }

            this.isMobileToolbarOpen = true;
        },

        closeMobileToolbar() {
            this.isMobileToolbarOpen = false;
        },

        handleSidebarToggle() {
            if (this.isMobile) {
                this.closeMobileToolbar();
                return;
            }

            this.setWorkspaceCollapsed(
                !this.isWorkspaceCollapsed
            );
        }
    }
};
</script>

<style lang="scss" scoped>
@import url("../../../assets/fonts/simplicity/stylesheet.css");

.lymphomap_main {
    width: 100%;
    min-width: 0;
    overflow: hidden;
    font-family:
        -apple-system,
        BlinkMacSystemFont,
        "Segoe UI",
        "Helvetica Neue",
        Arial,
        sans-serif,
        "Apple Color Emoji",
        "Segoe UI Emoji",
        "Segoe UI Symbol",
        "Noto Color Emoji";
}

.workspaceFrame {
    position: relative;
    display: flex;
    width: 100%;
    height: calc(100vh - 110px);
    height: calc(100dvh - 110px);
    min-width: 0;
    margin: 0 auto;
    overflow: hidden;
    align-items: stretch;
    flex-wrap: nowrap;
    clear: both;

    >.workspace_main_content {
        width: auto;
        min-width: 0;
        height: 100%;
        overflow: hidden;
        flex: 1 1 0%;
    }

    >.workspace_right_sidebar {
        min-width: 0;
        height: 100%;
    }
}

@media only screen and (max-width: 767px) {
    .workspaceFrame {
        position: relative;
        display: block;
        overflow: hidden;

        >.workspace_main_content,
        >.workspace_right_sidebar {
            position: absolute !important;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
            display: block !important;
            width: 100% !important;
            height: 100%;
            max-width: none;
            padding-left: 0;
            transition:
                transform 280ms cubic-bezier(0.4, 0, 0.2, 1);
            backface-visibility: hidden;
        }

        >.workspace_main_content {
            z-index: 1;
            transform: translate3d(0, 0, 0);
        }

        >.workspace_right_sidebar {
            z-index: 2;
            pointer-events: none;
            transform: translate3d(100%, 0, 0);
        }

        &.mobile_tools_open {
            >.workspace_main_content {
                pointer-events: none;
                transform: translate3d(-100%, 0, 0);
            }

            >.workspace_right_sidebar {
                pointer-events: auto;
                transform: translate3d(0, 0, 0);
            }
        }
    }
}

@media (prefers-reduced-motion: reduce) {
    .workspaceFrame {

        >.workspace_main_content,
        >.workspace_right_sidebar {
            transition: none;
        }
    }
}
</style>